/**
 * <service-ticker>: the thin service bar on product pages.
 * It scrolls slowly by itself in an endless loop (two identical groups) and can also be
 * scrolled by hand: swipe on touch screens, drag or wheel with a mouse. Auto-scroll pauses
 * while the visitor interacts or hovers and resumes shortly after. No auto-scroll with reduced motion.
 */
class ServiceTicker extends HTMLElement {
  connectedCallback() {
    this.group = this.querySelector('.service-ticker__group');
    if (!this.group) return;
    this.speed = 32; // pixels per second
    this.pausedUntil = 0;
    this.hover = false;
    this.pos = this.scrollLeft;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.pause = (ms = 2500) => { this.pausedUntil = performance.now() + ms; };
    this.onEnter = () => { this.hover = true; };
    this.onLeave = () => { this.hover = false; this.pause(800); };
    this.onUser = () => this.pause();
    this.onScroll = () => {
      if (this.selfScroll) { this.selfScroll = false; return; }
      this.pos = this.scrollLeft;
      this.wrap();
      this.pause();
    };

    // Mouse drag (touch uses native scrolling).
    this.onDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.drag = { x: event.clientX, left: this.scrollLeft, moved: false };
      this.pause(60000);
    };
    this.onMove = (event) => {
      if (!this.drag) return;
      const dx = event.clientX - this.drag.x;
      if (Math.abs(dx) > 4) { this.drag.moved = true; this.classList.add('is-dragging'); }
      this.scrollLeft = this.drag.left - dx;
    };
    this.onUp = () => {
      if (!this.drag) return;
      this.justDragged = this.drag.moved;
      this.drag = null;
      this.classList.remove('is-dragging');
      this.pause();
    };
    this.onClick = (event) => {
      if (this.justDragged) { event.preventDefault(); event.stopPropagation(); this.justDragged = false; }
    };

    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('touchstart', this.onUser, { passive: true });
    this.addEventListener('wheel', this.onUser, { passive: true });
    this.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    this.addEventListener('click', this.onClick, true);
    this.addEventListener('dragstart', (event) => event.preventDefault());

    if (this.reduced) return;
    this.last = performance.now();
    const tick = (now) => {
      const dt = Math.min(now - this.last, 100) / 1000;
      this.last = now;
      if (!this.hover && !this.drag && now > this.pausedUntil) {
        this.pos += this.speed * dt;
        this.wrap();
        this.selfScroll = true;
        this.scrollLeft = this.pos;
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  /** Keep the position inside the first group so the loop never runs out. */
  wrap() {
    const width = this.group.offsetWidth;
    if (!width) return;
    if (this.pos >= width) { this.pos -= width; this.selfScroll = true; this.scrollLeft = this.pos; }
    else if (this.pos < 0) { this.pos += width; this.selfScroll = true; this.scrollLeft = this.pos; }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
  }
}

if (!customElements.get('service-ticker')) customElements.define('service-ticker', ServiceTicker);
